/**
 * The work-time history as a list (ADR 0021): who changed what, from what to what, when and why.
 * One component for the shift drawer, the work-time panel and Min side, so the record reads the
 * same wherever a person meets it.
 */
import type { ApiWorkTimeChange } from '@/api/data';
import { T } from '@/i18n';
import { oslo } from '@/rules';
import { fmtD, fmtDs } from '@/ui/format';
import { addDays } from '@timefly/core';

const ACTION: Record<string, string> = {
  punch_in: 'Stemplet inn',
  punch_out: 'Stemplet ut',
  lunch_in: 'Pause start',
  lunch_out: 'Pause slutt',
  created: 'Registrert manuelt',
  changed: 'Endret',
  approved: 'Godkjent',
  unapproved: 'Godkjenning trukket',
  deleted: 'Slettet',
  absence_requested: 'Fravær søkt',
  absence_registered: 'Fravær registrert',
  absence_changed: 'Fravær endret',
  absence_approved: 'Fravær innvilget',
  absence_declined: 'Fravær avslått',
  absence_withdrawn: 'Fravær trukket tilbake',
  absence_deleted: 'Fravær slettet',
  absence_cancel_requested: 'Ønsker å avlyse',
};
// #166: what a field is called in a sentence; times are read by clockText below
const FIELD: Record<string, string> = {
  punch_in_time: 'Innstempling',
  punch_out_time: 'Utstempling',
  lunch_in_time: 'Pause start',
  lunch_out_time: 'Pause slutt',
  status: 'Status',
  start_date: 'Fra-dato',
  end_date: 'Til-dato',
  absence_type_id: 'Type',
  charged_days: 'Trukket dager',
};
const STATUS: Record<string, string> = { pending: 'venter', accepted: 'innvilget', declined: 'avslått' };
const ORDER = ['punch_in_time', 'lunch_in_time', 'lunch_out_time', 'punch_out_time', 'is_approved'];
// a stored instant; a time-like string that is not a real instant is shown as stored (CH166-02)
const isTime = (v: unknown): v is string =>
  typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v) && !Number.isNaN(Date.parse(v));

/**
 * A stored value as a person reads it. With the shift's own `day` a time is its clock on that day,
 * «neste dag» on the day after and with its date otherwise. Without it, every time keeps its own date:
 * a list of many shifts does not know the shift, and a punch's date is not the shift's (CH166-01).
 * Anything else that is not plain text is shown as stored.
 */
export function valueText(v: unknown, day?: string): string {
  if (typeof v === 'boolean') return v ? T('ja') : T('nei');
  if (isTime(v)) {
    const o = oslo(v);
    if (!o) return v;
    if (day && o.date === day) return o.time;
    if (day && o.date === addDays(day, 1)) return `${o.time} ${T('neste dag')}`;
    return `${fmtDs(o.date)} kl. ${o.time}`;
  }
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) return fmtD(v);
  if (typeof v === 'string' && STATUS[v]) return T(STATUS[v]);
  if (typeof v === 'string' || typeof v === 'number') return String(v);
  return JSON.stringify(v) ?? String(v);
}

const minutes = (a: string, b: string) => Math.round((Date.parse(b) - Date.parse(a)) / 60_000);

/**
 * One event's changes as sentences (#166): «Innstempling lagt til: 12:11», «… fjernet», «… endret
 * fra 12:11 til 12:30», one per changed field, in clock order. A break with both ends in the event
 * reads in minutes. A payload of another shape is shown as stored. `day` is the shift's day.
 */
export function changeLines(c: ApiWorkTimeChange, day?: string): string[] {
  const ch = (c.changes ?? {}) as Record<string, unknown>;
  const v = (x: unknown) => valueText(x, day);
  const out: string[] = [];
  // both ends of a break read as one line; then they are not lines of their own
  let paused = false;
  const li = ch.lunch_in_time;
  const lo = ch.lunch_out_time;
  // only two complete [old, new] pairs are read as one break; anything else is shown field by field, as stored
  if (Array.isArray(li) && li.length === 2 && Array.isArray(lo) && lo.length === 2) {
    const [a0, a1] = li;
    const [b0, b1] = lo;
    const had = isTime(a0) && isTime(b0);
    const has = isTime(a1) && isTime(b1);
    const span = (s: string, e: string) =>
      minutes(s, e) ? `${v(s)} ${T('til {day}', { day: v(e) })}` : v(s);
    if (!had && a0 == null && b0 == null && has) {
      out.push(`${T('Pause lagt til')}: ${minutes(a1, b1)} min, ${span(a1, b1)}`);
      paused = true;
    } else if (had && a1 == null && b1 == null) {
      out.push(T('Pause fjernet'));
      paused = true;
    } else if (had && has) {
      const [m0, m1] = [minutes(a0, b0), minutes(a1, b1)];
      out.push(
        m0 === m1
          ? `${T('Pause flyttet fra')} ${v(a0)} ${T('til {day}', { day: v(a1) })}, ${m1} min`
          : `${T('Pause endret fra')} ${m0} ${T('til {day}', { day: `${m1} min` })}`,
      );
      paused = true;
    }
  }
  const keys = Object.keys(ch)
    .filter((k) => !(paused && (k === 'lunch_in_time' || k === 'lunch_out_time')))
    .sort((a, b) => {
      const [i, j] = [ORDER.indexOf(a), ORDER.indexOf(b)];
      return (i < 0 ? 99 : i) - (j < 0 ? 99 : j);
    });
  for (const k of keys) {
    const p = ch[k];
    const name = T(FIELD[k] ?? k);
    if (!Array.isArray(p) || p.length !== 2) {
      out.push(`${name}: ${typeof p === 'string' ? p : JSON.stringify(p)}`);
      continue;
    }
    const [from, to] = p;
    if (k === 'is_approved') {
      // an approval event names itself; inside a correction the flag is a line of its own
      if (c.action === 'approved' || c.action === 'unapproved' || !!from === !!to) continue;
      out.push(T(to ? 'Godkjent' : 'Godkjenning trukket'));
      continue;
    }
    if (from == null && to == null) continue;
    if (from == null) out.push(`${name} ${T('lagt til')}: ${v(to)}`);
    else if (to == null) out.push(`${name} ${T('fjernet')}`);
    else out.push(`${name} ${T('endret fra')} ${v(from)} ${T('til {day}', { day: v(to) })}`);
  }
  return out;
}

/**
 * A correction leads with what changed (#166); any other event with its own name, the changes under
 * it. Then the reason, who and when, as recorded: none of them is filled in when missing.
 */
export default function History({
  rows,
  empty,
  day,
}: { rows: ApiWorkTimeChange[]; empty?: string; day?: string }) {
  if (!rows.length) return <p className="hint">{empty ?? T('Ingen endringer registrert.')}</p>;
  return (
    <ol className="hist" data-testid="work-time-history">
      {[...rows]
        .sort((a, b) => b.created_at.localeCompare(a.created_at))
        .map((c) => {
          const at = Number.isNaN(Date.parse(c.created_at)) ? null : oslo(c.created_at);
          const lines = changeLines(c, day);
          const lead = c.action === 'changed' && lines.length > 0;
          const who = [c.changed_by_name, at ? `${fmtD(at.date)} kl. ${at.time}` : '']
            .filter(Boolean)
            .join(' · ');
          return (
            <li key={c.id}>
              {lead ? (
                lines.map((l) => (
                  <b key={l} className="hl">
                    {l}
                  </b>
                ))
              ) : (
                <>
                  <b className="hl">{T(ACTION[c.action] ?? c.action)}</b>
                  {lines.map((l) => (
                    <span key={l} className="hl">
                      {l}
                    </span>
                  ))}
                </>
              )}
              {c.reason && <span className="hl">«{c.reason}»</span>}
              {who && <span className="hl sub">{who}</span>}
            </li>
          );
        })}
    </ol>
  );
}
